import styled from 'styled-components';
import { AttributeGroupModel } from '@upright/common/model/input/companies/products/ipr/attribute-groups';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';

/** Render one titled row of attribute chips, or nothing when the group is empty. */
export function AttributeGroup({ title, chips }: Pick<AttributeGroupModel, 'title' | 'chips'>) {
  if (chips.length === 0) return null;
  return (
    <AttributeRow>
      <AttributeRowTitle>{title}</AttributeRowTitle>
      {chips.map((chip, index) => (
        <AttributeChip
          key={index}
          $inherited={chip.inherited}
          $manual={chip.manual}
          title={chip.manual ? 'Set manually' : undefined}
        >
          {chip.label}
          {chip.inherited && <InheritedTag>(inherited)</InheritedTag>}
        </AttributeChip>
      ))}
    </AttributeRow>
  );
}

const Chip = styled.div`
  padding: var(--space__xs);
  border-radius: var(--border-radius__md);
`;

const AttributeRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--space__xs);
  align-items: center;
  flex-wrap: wrap;
`;

const AttributeRowTitle = styled.div`
  font-family: var(font-family-heading);
  text-transform: uppercase;
  text-wrap: nowrap;
  color: var(--fgColor-muted);
  font-weight: 400;
`;

// Inherited-only values get the same secondary/italic treatment the taxonomy-mapping cell uses for
// inherited mappings (PLT-5695): muted color + italic, plus a trailing "(inherited)" tag.
// Manually set values take the orange of a manual match in the product selector (`CoreProductChip`).
const AttributeChip = styled(Chip)<{ $inherited?: boolean; $manual?: boolean }>`
  background-color: ${props => (props.$manual ? 'var(--platform-orange-100)' : 'var(--platform-gray-100)')};
  color: ${props =>
    props.$manual
      ? 'var(--platform-orange-900)'
      : props.$inherited
        ? BouColor.TOOL_TEXT_DARK_GRAY
        : 'var(--neutral-700)'};
  font-style: ${props => (props.$inherited ? 'italic' : 'normal')};
  text-wrap: nowrap;
`;

const InheritedTag = styled.span`
  margin-left: 0.35em;
`;
